export default class SeasonPassClient {

    constructor(game, network) {
        this.game = game
        this.network = network

        this.events = new Phaser.Events.EventEmitter()

        this.state = null
        this.progress = null
        this.tasks = {
            daily: [],
            weekly: [],
            dailyResetAt: null,
            weeklyResetAt: null
        }

        this.bootstrapTimer = null
        this.ready = false
        this.overlayVisible = false
    }

    setOverlayVisible(isVisible) {
        this.overlayVisible = Boolean(isVisible)
    }

    isOverlayVisible() {
        return this.overlayVisible
    }

    handleLoginBootstrap() {
        this.ready = false
        this.scheduleBootstrapRequest()
    }

    scheduleBootstrapRequest() {
        this.clearBootstrapTimer()
        this.bootstrapTimer = setTimeout(() => {
            if (!this.ready) {
                this.requestState()
            }
        }, 2500)
    }

    clearBootstrapTimer() {
        if (this.bootstrapTimer) {
            clearTimeout(this.bootstrapTimer)
            this.bootstrapTimer = null
        }
    }

    requestState() {
        this.network.send('season_pass_get_state')
    }

    claimReward(track, level) {
        this.network.send('season_pass_claim_reward', { track, level })
    }

    updateState(payload) {
        if (!payload) {
            return
        }

        this.state = {
            season: payload.season,
            tracks: payload.tracks,
            levels: payload.levels
        }

        if (payload.tasks) {
            this.updateTasks(payload.tasks, true)
        }

        if (payload.progress) {
            this.updateProgress(payload.progress, true)
        }

        this.ready = true
        this.clearBootstrapTimer()

        const snapshot = this.getSnapshot()
        this.events.emit('state', snapshot)
        this.game.events.emit('season-pass:state', snapshot)
    }

    updateProgress(progressPayload, silent = false) {
        if (!progressPayload) {
            return
        }

        this.progress = {
            ...(this.progress || {}),
            ...progressPayload
        }

        if (!silent) {
            this.events.emit('progress', this.progress)
            this.game.events.emit('season-pass:progress', this.progress)
        }
    }

    updateTasks(taskPayload, silent = false) {
        if (!taskPayload) {
            return
        }

        const reason = taskPayload.reason || null
        const nextTasks = {
            daily: this.tasks.daily || [],
            weekly: this.tasks.weekly || [],
            dailyResetAt: taskPayload.dailyResetAt || this.tasks.dailyResetAt || null,
            weeklyResetAt: taskPayload.weeklyResetAt || this.tasks.weeklyResetAt || null
        }

        if (Array.isArray(taskPayload.daily)) {
            const shouldMergeDaily = !silent && reason === 'activity' && Array.isArray(nextTasks.daily) && nextTasks.daily.length
            nextTasks.daily = shouldMergeDaily
                ? this.mergeTaskLists(nextTasks.daily, taskPayload.daily)
                : this.cloneTaskList(taskPayload.daily)
        } else if (!Array.isArray(nextTasks.daily)) {
            nextTasks.daily = []
        }

        if (Array.isArray(taskPayload.weekly)) {
            const shouldMergeWeekly = !silent && reason === 'activity' && Array.isArray(nextTasks.weekly) && nextTasks.weekly.length
            nextTasks.weekly = shouldMergeWeekly
                ? this.mergeTaskLists(nextTasks.weekly, taskPayload.weekly)
                : this.cloneTaskList(taskPayload.weekly)
        } else if (!Array.isArray(nextTasks.weekly)) {
            nextTasks.weekly = []
        }

        this.tasks = nextTasks
        this.tasks.reason = reason

        if (!silent) {
            this.events.emit('tasks', this.tasks)
            this.game.events.emit('season-pass:tasks', this.tasks)
        }
    }

    mergeTaskLists(currentList = [], updates = []) {
        if (!updates.length) {
            return currentList.map(task => ({ ...task }))
        }

        const mergedOrder = []
        const byId = new Map()

        currentList.forEach(task => {
            if (!task || !task.id) {
                return
            }
            const copy = { ...task }
            byId.set(task.id, copy)
            mergedOrder.push(task.id)
        })

        updates.forEach(task => {
            if (!task || !task.id) {
                return
            }

            if (byId.has(task.id)) {
                Object.assign(byId.get(task.id), task)
                return
            }

            const copy = { ...task }
            byId.set(task.id, copy)
            mergedOrder.push(task.id)
        })

        return mergedOrder
            .map(id => byId.get(id))
            .filter(Boolean)
    }

    cloneTaskList(list = []) {
        return list.map(task => ({ ...task }))
    }

    handleRewardClaim(payload) {
        if (!payload) {
            return
        }

        if (payload.progress) {
            this.updateProgress(payload.progress)
        }

        this.events.emit('reward-claimed', payload)
        this.game.events.emit('season-pass:reward-claimed', payload)
    }

    handleError(errorPayload) {
        if (!errorPayload) {
            return
        }

        this.events.emit('error', errorPayload)
        this.game.events.emit('season-pass:error', errorPayload)
    }

    on(event, callback, context) {
        this.events.on(event, callback, context)
    }

    off(event, callback, context) {
        this.events.off(event, callback, context)
    }

    destroy() {
        this.clearBootstrapTimer()
        this.events.removeAllListeners()
    }

    getSnapshot() {
        return {
            state: this.state,
            progress: this.progress,
            tasks: this.tasks
        }
    }

}
